import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  DriveEta as OutIcon,
  HomeWork as ReturnedIcon,
  Build as MaintenanceIcon,
  LocalCarWash as WashIcon,
  LocalParking as ParkingIcon,
  NoteAdd as NoteIcon,
  Info as InfoIcon,
  Build as BuildIcon,
  CameraAlt as CameraIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Vehicle, VehicleStatus } from '../../types';
import StatusUpdateModal from '../../components/FieldAgent/StatusUpdateModal';
import ParkingMapModal from '../../components/FieldAgent/ParkingMapModal';
import NoteForm from '../../components/FieldAgent/NoteForm';
import MaintenanceManagement from '../../components/FieldAgent/MaintenanceManagement';
import InterventionManagement from '../../components/FieldAgent/InterventionManagement';
import ConditionAssessmentModal from '../../components/FieldAgent/ConditionAssessmentModal';
import { getStatusColor, getStatusLabel } from '../../utils/vehicleUtils';
import { api } from '../../services/api';

interface VehicleQuickActionsProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
}

const VehicleQuickActions: React.FC<VehicleQuickActionsProps> = ({
  vehicle,
  open,
  onClose
}) => {
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showParkingModal, setShowParkingModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showInterventionModal, setShowInterventionModal] = useState(false);
  const [showConditionAssessmentModal, setShowConditionAssessmentModal] = useState(false);
  const [quickStatus, setQuickStatus] = useState<VehicleStatus | null>(null);
  const [overdueMaintenance, setOverdueMaintenance] = useState<any>(null);
  const [loadingOverdue, setLoadingOverdue] = useState(false);

  const handleQuickStatus = (status: VehicleStatus) => {
    setQuickStatus(status);
    setShowStatusModal(true);
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/field-agent/vehicles/${vehicle.id}`);
  };

  // Fetch overdue maintenance when modal opens
  useEffect(() => {
    if (open && vehicle) {
      setLoadingOverdue(true);
      api.get(`/vehicles/${vehicle.id}/overdue-maintenance`)
        .then(response => {
          setOverdueMaintenance(response.data.data);
        })
        .catch(error => {
          console.error('Error fetching overdue maintenance:', error);
          setOverdueMaintenance(null);
        })
        .finally(() => {
          setLoadingOverdue(false);
        });
    }
  }, [open, vehicle]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3, m: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Vehicle Photo */}
              {vehicle.primary_image_url && (
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: 2,
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: '2px solid #e0e0e0'
                }}>
                  <img
                    src={vehicle.primary_image_url}
                    alt={`${vehicle.registration_number}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              )}
              
              {/* Vehicle Info */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {vehicle.registration_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicle.marque} {vehicle.modele} {vehicle.year}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Current Status and Location */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Statut
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getStatusLabel(vehicle.status)}
                    sx={{
                      bgcolor: getStatusColor(vehicle.status),
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Place de parking
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {vehicle.parking_spot?.spot_number || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Quick Status Actions */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            ACTIONS RAPIDES
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<OutIcon />}
                onClick={() => handleQuickStatus('out')}
                sx={{ 
                  height: 64, 
                  justifyContent: 'flex-start', 
                  px: 2,
                  textAlign: 'left'
                }}
              >
                Sortir
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<ReturnedIcon />}
                onClick={() => handleQuickStatus('returned')}
                sx={{ 
                  height: 64, 
                  justifyContent: 'flex-start', 
                  px: 2,
                  textAlign: 'left'
                }}
              >
                Rentrer
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<MaintenanceIcon />}
                onClick={() => handleQuickStatus('under_maintenance')}
                sx={{ 
                  height: 64, 
                  justifyContent: 'flex-start', 
                  px: 2,
                  textAlign: 'left'
                }}
              >
                Maintenance
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<WashIcon />}
                onClick={() => handleQuickStatus('washing')}
                sx={{ 
                  height: 64, 
                  justifyContent: 'flex-start', 
                  px: 2,
                  textAlign: 'left'
                }}
              >
                Lavage
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* État des Lieux - Carte avec image et bouton */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2,
            }}
          >
            {/* Image de fond - Dashboard futuriste */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '300px',
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/fieldagent/etatsdeslieux.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 3,
              }}
            >
              {/* Overlay sombre pour meilleure lisibilité */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 0,
                }}
              />
              {/* Texte "ÉTATS DES LIEUX" dans une boîte en haut */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  px: 3,
                  py: 1.5,
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    textAlign: 'center',
                  }}
                >
                  ÉTATS DES LIEUX
                </Typography>
              </Box>
            </Box>
            
            {/* Carte transparente en bas */}
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 2,
                p: 3,
                mt: -3,
                position: 'relative',
                zIndex: 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: '1rem',
                  fontWeight: 400,
                }}
              >
                Commencer un état des lieux de véhicule.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setShowConditionAssessmentModal(true)}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: '#9c27b0',
                  color: '#9c27b0',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1.5,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(156, 39, 176, 0.04)',
                    borderColor: '#7b1fa2',
                    color: '#7b1fa2',
                  }
                }}
              >
                Commencer
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Other Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<ParkingIcon />}
              onClick={() => setShowParkingModal(true)}
              sx={{ justifyContent: 'flex-start' }}
            >
              Changer de place/voir l'emplacement
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<NoteIcon />}
              onClick={() => setShowNoteModal(true)}
              sx={{ justifyContent: 'flex-start' }}
            >
              Ajouter Note Technique
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<MaintenanceIcon />}
              onClick={() => setShowMaintenanceModal(true)}
              sx={{ justifyContent: 'flex-start' }}
            >
              Gérer Maintenance
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<BuildIcon />}
              onClick={() => setShowInterventionModal(true)}
              sx={{ justifyContent: 'flex-start' }}
            >
              Gérer Interventions
            </Button>

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<InfoIcon />}
              onClick={handleViewDetails}
              sx={{ justifyContent: 'flex-start' }}
            >
              Voir Détails Complets
            </Button>
          </Box>

          {/* Overdue Maintenance Alert Zone */}
          {overdueMaintenance && overdueMaintenance.has_overdue_maintenance && (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity="error" 
                sx={{ 
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    color: '#d32f2f',
                    fontWeight: 600
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Maintenance en retard ({overdueMaintenance.overdue_count} tâche{overdueMaintenance.overdue_count > 1 ? 's' : ''})
                </Typography>
                {overdueMaintenance.alerts.map((alert: any, index: number) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {alert.message}
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      {showStatusModal && (
        <StatusUpdateModal
          vehicle={vehicle}
          initialStatus={quickStatus || vehicle.status}
          open={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setQuickStatus(null);
          }}
          onSuccess={() => {
            setShowStatusModal(false);
            setQuickStatus(null);
            onClose();
          }}
        />
      )}

      {/* Parking Map Modal */}
      {showParkingModal && (
        <ParkingMapModal
          vehicle={vehicle}
          open={showParkingModal}
          onClose={() => setShowParkingModal(false)}
          onReassign={async (vehicleId, newSpotId) => {
            // Handle the reassignment
            console.log(`Reassigning vehicle ${vehicleId} to spot ${newSpotId}`);
            // Close the modal after successful reassignment
            setShowParkingModal(false);
            onClose();
          }}
        />
      )}

      {/* Note Form Modal */}
      {showNoteModal && (
        <NoteForm
          vehicle={vehicle}
          open={showNoteModal}
          onClose={() => setShowNoteModal(false)}
          onSuccess={() => {
            setShowNoteModal(false);
            onClose();
          }}
        />
      )}

      {/* Maintenance Management Modal */}
      {showMaintenanceModal && (
        <MaintenanceManagement
          vehicle={vehicle}
          open={showMaintenanceModal}
          onClose={() => setShowMaintenanceModal(false)}
          onSuccess={() => {
            setShowMaintenanceModal(false);
            onClose();
          }}
        />
      )}

      {/* Intervention Management Modal */}
      {showInterventionModal && (
        <InterventionManagement
          vehicle={vehicle}
          open={showInterventionModal}
          onClose={() => setShowInterventionModal(false)}
          onSuccess={() => {
            setShowInterventionModal(false);
            onClose();
          }}
        />
      )}

      {/* Condition Assessment Modal */}
      {showConditionAssessmentModal && (
        <ConditionAssessmentModal
          vehicle={vehicle}
          open={showConditionAssessmentModal}
          onClose={() => setShowConditionAssessmentModal(false)}
          onSuccess={() => {
            setShowConditionAssessmentModal(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default VehicleQuickActions;

